import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { formatDate, formatWorkingHoursDuration, getLocalizedRoleName } from "../../../../utils/Helpers";
import { getLocalizedShiftName } from "../../../../utils/eventShiftOptions";
import { TIMESHEET_CHECKOUT_STATUSES } from "./constants";
import { UIPaginationBar, UITable, UIRowActionsMenu } from "../../../ui";
import TimesheetCheckoutStatusBadge from "./TimesheetCheckoutStatusBadge";
import TimesheetEmptyState from "./TimesheetEmptyState";
import { resolveCheckoutStatus } from "./timesheetUtils";

import "./EventTimesheet.scss";

const EventTimesheetShiftsTable = ({
  copy,
  timeSheetCopy,
  sharedLabels,
  applicantsLabels,
  lang,
  rows,
  isLoading,
  page,
  pageSize,
  total,
  pages,
  rangeFrom,
  rangeTo,
  rowActions,
  onRowAction,
  onPageChange,
  onPageSizeChange,
}) => {
  const tableRows = useMemo(
    () =>
      (rows || []).map((row, index) => ({
        ...row,
        id: row.id ?? `${row.check_in_at}-${index}`,
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "date",
        headerName: timeSheetCopy.date,
        sortable: false,
        autoFlex: false,
        flex: 0,
        minWidth: 170,
        cellClassName: "event-timesheet__date-cell",
        valueGetter: (params) =>
          params.row.check_in_readable_date || params.row.readable_date || "-",
      },
      {
        field: "check_in_at",
        headerName: timeSheetCopy.checkin,
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.value ? formatDate(params.value, "HH:mm:ss") : "-",
      },
      {
        field: "check_out_at",
        headerName: timeSheetCopy.checkout,
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.value ? formatDate(params.value, "HH:mm:ss") : "-",
      },
      {
        field: "role",
        headerName: timeSheetCopy.role,
        width: 120,
        sortable: false,
        valueGetter: (params) =>
          getLocalizedRoleName(params.row?.role, lang) || "-",
      },
      {
        field: "shift",
        headerName: copy.shift,
        width: 120,
        sortable: false,
        valueGetter: (params) =>
          getLocalizedShiftName(params.row?.shift, lang, sharedLabels) || "-",
      },
      {
        field: "working_hours",
        headerName: copy.workingHoursPerDay,
        sortable: false,
        valueFormatter: ({ value }) => formatWorkingHoursDuration(value, lang),
      },
      {
        field: "checkout_status",
        headerName: timeSheetCopy.monthlyReport.checkoutStatus,
        width: 160,
        sortable: false,
        renderCell: (params) => {
          const checkoutStatus = resolveCheckoutStatus(params.row);
          const label =
            checkoutStatus === TIMESHEET_CHECKOUT_STATUSES.auto
              ? timeSheetCopy.monthlyReport.automaticCheckout
              : checkoutStatus === TIMESHEET_CHECKOUT_STATUSES.self
                ? timeSheetCopy.monthlyReport.selfCheckout
                : timeSheetCopy.monthlyReport.checkoutStatusPending;

          return (
            <TimesheetCheckoutStatusBadge status={checkoutStatus} label={label} />
          );
        },
      },
      ...(rowActions?.length
        ? [
            {
              field: "actions",
              headerName: sharedLabels.actions,
              width: 80,
              sortable: false,
              renderCell: (params) => (
                <UIRowActionsMenu
                  items={rowActions.map((action) => ({
                    ...action,
                    onClick: () => onRowAction?.(action.key, params.row),
                  }))}
                />
              ),
            },
          ]
        : []),
    ],
    [copy, timeSheetCopy, sharedLabels, lang, rowActions, onRowAction],
  );

  if (!isLoading && !tableRows.length) {
    return <TimesheetEmptyState variant="shifts" copy={copy} />;
  }

  return (
    <div className="event-timesheet__table-shell">
      <UITable
        rows={tableRows}
        columns={columns}
        loading={isLoading}
        pageSize={pageSize}
        contentSized
        hideFooter
        rowHeight={58}
        getRowClassName={(params) =>
          params.row.is_checked_out_automatically
            ? "event-timesheet__row event-timesheet__row--flagged"
            : "event-timesheet__row"
        }
      />
      {total > 0 ? (
        <UIPaginationBar
          currentPage={page}
          pages={pages}
          pageSize={pageSize}
          rangeTemplate={applicantsLabels.rangeTemplate}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          rangeTotal={total}
          rowsPerPageLabel={applicantsLabels.rowsPerPageLabel}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          className="event-timesheet__pagination"
        />
      ) : null}
    </div>
  );
};

EventTimesheetShiftsTable.propTypes = {
  copy: PropTypes.object.isRequired,
  timeSheetCopy: PropTypes.object.isRequired,
  sharedLabels: PropTypes.object.isRequired,
  applicantsLabels: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  pages: PropTypes.number,
  rangeFrom: PropTypes.number,
  rangeTo: PropTypes.number,
  rowActions: PropTypes.array,
  onRowAction: PropTypes.func,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};

EventTimesheetShiftsTable.defaultProps = {
  rows: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  total: 0,
  pages: 1,
  rangeFrom: 0,
  rangeTo: 0,
  rowActions: [],
  onRowAction: undefined,
};

export default EventTimesheetShiftsTable;
